/* DC search results page behaviour (added 2026-09-22, precision + SKU + exact-tag scopes 2026-09-29).
 * - Old links that still use the pre-2026-09-29 scope syntax are rewritten once to the new scope.
 * - Raw queries (no scope in q) are redirected to the right tab (auto-switch to Used & Open Box /
 *   Packages when the customer's words say so). A query that looks like a SKU and matches one
 *   variant exactly goes straight to that product page.
 * - Best matches: products whose title/brand contain EVERY word typed (sold-out ones included) are
 *   pinned to the top of page 1 when there are only a few of them (a specific product search).
 * - Fetches result counts for the other tabs via sections/dc-search-count.liquid.
 * - Suggests matching categories (collections) under the tabs on the Products tab.
 * - Fills the "we did find matches here" list on the no-results block.
 * Scope strings come from snippets/dc-search-scope.liquid via data-scopes.
 */

/* Shared helpers - identical copy in assets/predictive-search.js (either file works alone). */
window.dcSearchIntent = window.dcSearchIntent || (() => {
  const patterns = {
    used: /\b(used|pre[\s-]?owned|second[\s-]?hand|refurbished|open[\s-]?box)\b/i,
    packages: /\b(packages?|bundles?)\b/i
  };
  const stopWords = new Set(['a', 'an', 'and', 'the', 'for', 'with', 'of', 'in', 'on', 'to', 'by', 'or', 'not', 'w']);
  return {
    detect(term) {
      if (patterns.used.test(term)) return 'used';
      if (patterns.packages.test(term)) return 'packages';
      return 'products';
    },
    strip(term, tab) {
      const re = patterns[tab];
      if (!re) return term;
      const stripped = term.replace(new RegExp(re.source, 'gi'), ' ').replace(/\s+/g, ' ').trim();
      return stripped || term;
    },
    /**
     * One "word" that looks like a SKU: a digit plus SKU punctuation, or 5+ digits
     * (96.102.110, SPRO-24.714.130+LN, TC-407, 48211414030) - not model names like mk25, d530, x50i.
     */
    isSkuLike(term) {
      const t = (term || '').trim();
      if (t.length < 4 || !/\d/.test(t) || !/^[A-Za-z0-9][A-Za-z0-9.\-+/_#]*$/.test(t)) return false;
      return /[.\-+/_#]/.test(t) || (t.match(/\d/g) || []).length >= 5;
    },
    /**
     * "Every word must be in the title or the brand": (title:w1 OR vendor:w1) (title:w2 OR vendor:w2).
     * Shopify ANDs fielded terms (plain words are ORed), so this only returns close matches.
     * A SKU-looking query becomes (variants.sku:X OR title:X OR vendor:X). Returns '' when nothing is usable.
     */
    preciseQuery(term) {
      const sku = (term || '').trim();
      // A SKU/model number: its SKU tokens or title (plain words next to a -tag: exclusion match SKUs loosely).
      if (this.isSkuLike(sku)) return `(variants.sku:${sku} OR title:${sku} OR vendor:${sku})`;
      // Shopify keeps dots and apostrophes inside a word ("4.0", "7.5mm", "4080.03", "n'roll" are one
      // token) and splits on spaces, hyphens and other punctuation - split the same way.
      const words = (term || '')
        .toLowerCase()
        .replace(/’/g, "'")
        .split(/[^\p{L}\p{N}.']+/u)
        .map((w) => w.replace(/^[.']+|[.']+$/g, ''))
        .filter((w) => w && !stopWords.has(w));
      const unique = [...new Set(words)].slice(0, 8);
      return unique.map((w) => `(title:${w} OR vendor:${w})`).join(' ');
    },
    /** Renders sections/dc-search-pins for q. Resolves to { count, items: [li, ...] }. */
    async fetchPins(searchUrl, q) {
      const url = new URL(searchUrl || '/search', window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', q);
      url.searchParams.set('section_id', 'dc-search-pins');
      const response = await fetch(url.toString());
      if (!response.ok) return { count: 0, items: [] };
      const tmpl = document.createElement('template');
      tmpl.innerHTML = await response.text();
      const root = tmpl.content.querySelector('[data-dc-pins]');
      if (!root) return { count: 0, items: [] };
      return {
        count: parseInt(root.dataset.count, 10) || 0,
        items: [...root.querySelectorAll('li[data-dc-handle]')]
      };
    },
    /**
     * For a SKU-looking query: { url, tab } when exactly one product has a variant with that exact SKU,
     * else null (the caller then searches normally). Two lookups in parallel: variants.sku: and the plain
     * word (a few SKUs aren't reachable through variants.sku:).
     */
    async resolveSku(searchUrl, term) {
      const sku = (term || '').trim();
      const results = await Promise.all([
        this.fetchPins(searchUrl, `variants.sku:${sku}`),
        this.fetchPins(searchUrl, sku)
      ]);
      const exact = new Map();
      results.forEach((res) => res.items.forEach((li) => {
        if (li.dataset.dcExactUrl && !exact.has(li.dataset.dcHandle)) exact.set(li.dataset.dcHandle, li);
      }));
      if (exact.size !== 1) return null;
      const [li] = exact.values();
      return { url: li.dataset.dcExactUrl, tab: li.dataset.dcKind };
    },
    /** Rejects after ms so a slow lookup never blocks a search. */
    withTimeout(promise, ms) {
      return Promise.race([promise, new Promise((resolve, reject) => { setTimeout(() => reject(new Error('timeout')), ms); })]);
    }
  };
})();

(() => {
  // Pin best matches only when there are this many or fewer (more means the words weren't specific).
  // Keep in step with dc_per_page in sections/dc-search-pins.liquid.
  const PIN_MAX = 24;

  const handleOf = (li) => {
    if (li.dataset.dcHandle) return li.dataset.dcHandle;
    const link = li.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const match = new URL(link.getAttribute('href'), window.location.origin).pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const init = () => {
    const nav = document.querySelector('[data-dc-search-tabs]');
    if (!nav) return;

    let scopes = {};
    try { scopes = JSON.parse(nav.dataset.scopes || '{}'); } catch (e) { scopes = {}; }
    const active = nav.dataset.active;
    const terms = (nav.dataset.terms || '').trim();
    const searchUrl = nav.dataset.searchUrl || '/search';
    const tabs = [...nav.querySelectorAll('a[data-tab]')];
    const current = new URL(window.location.href);
    const helper = window.dcSearchIntent;

    // 0. A link that still carries the pre-2026-09-29 scope: rewrite q to the current scope (keeps
    // filters, sort and page).
    if (nav.dataset.legacy === 'true' && scopes[active]) {
      current.searchParams.set('q', `${scopes[active]} ${terms}`.trim());
      window.location.replace(current.toString());
      return;
    }

    // 1. Raw query without a scope: send it to the right tab (one redirect, keeps sort).
    // Guard: never redirect a query that already carries search syntax (prevents loops).
    const hasSyntax = /(^|\s|\()-?(tag|product_type|title|vendor|variants\.sku|sku|body|type):/i.test(terms);
    if (active === 'all' && terms && !hasSyntax) {
      const goToTab = (tab, term) => {
        const params = new URLSearchParams();
        params.set('type', 'product');
        params.set('options[prefix]', 'last');
        params.set('q', `${scopes[tab] || scopes.products || ''} ${term}`.trim());
        const sortBy = current.searchParams.get('sort_by');
        if (sortBy && sortBy !== 'relevance') params.set('sort_by', sortBy);
        window.location.replace(`${searchUrl}?${params.toString()}`);
      };

      if (helper.isSkuLike(terms)) {
        // A SKU: straight to the product when exactly one variant has it, else the Products tab.
        helper.withTimeout(helper.resolveSku(searchUrl, terms), 2500)
          .then((hit) => {
            if (hit && hit.url) {
              window.location.replace(hit.url);
            } else {
              goToTab('products', terms);
            }
          })
          .catch(() => goToTab('products', terms));
        return;
      }

      const tab = helper.detect(terms);
      goToTab(tab, helper.strip(terms, tab));
      return;
    }

    // 2. Best matches: pin the few products whose title/brand contain every word to the top of the
    // results (sold-out included), so a specific product never hides behind hundreds of partial matches.
    const grid = document.querySelector('#filter-results ul[role="list"]');
    const page = parseInt(current.searchParams.get('page') || '1', 10) || 1;
    const filtered = [...current.searchParams.keys()].some((key) => key.startsWith('filter.'));
    const sortBy = current.searchParams.get('sort_by');
    const precise = helper.preciseQuery(terms);
    if (grid && precise && scopes[active] && !filtered && (!sortBy || sortBy === 'relevance')) {
      helper.fetchPins(searchUrl, `${scopes[active]} ${precise}`)
        .then(({ count, items }) => {
          if (!count || count > PIN_MAX || !items.length) return;
          const pinned = new Map(items.map((li) => [li.dataset.dcHandle, li]));
          const existing = new Map();
          [...grid.children].forEach((li) => {
            const handle = handleOf(li);
            if (handle && pinned.has(handle)) existing.set(handle, li);
          });
          if (page > 1) {
            // Later pages: drop products already pinned on page 1, unless that would empty the page.
            if (existing.size < grid.children.length) existing.forEach((li) => li.remove());
            return;
          }
          // Best matches already on page 1 keep their order and go first (reusing their cards keeps any
          // app-processed markup); the ones Shopify had pushed further down (usually sold out) follow.
          const fragment = document.createDocumentFragment();
          const append = (node) => {
            node.classList.add('dc-search-pin');
            fragment.appendChild(node);
          };
          existing.forEach((li) => append(li));
          pinned.forEach((li, handle) => {
            if (!existing.has(handle)) append(li);
          });
          grid.prepend(fragment);
        })
        .catch(() => {});
    }

    // 3. Counts for the other tabs.
    const counts = {};
    const emptyAlt = document.querySelector('[data-dc-empty-alt]');
    const renderEmptyAlt = () => {
      if (!emptyAlt) return;
      const list = emptyAlt.querySelector('ul');
      list.innerHTML = '';
      tabs.forEach((a) => {
        const key = a.dataset.tab;
        if (key === active || !counts[key]) return;
        const li = document.createElement('li');
        const link = document.createElement('a');
        link.className = 'link';
        link.href = a.href;
        link.textContent = `${counts[key]} ${counts[key] === 1 ? 'result' : 'results'} in ${a.dataset.label}`;
        li.appendChild(link);
        list.appendChild(li);
      });
      emptyAlt.hidden = list.children.length === 0;
    };

    tabs.forEach((a) => {
      const key = a.dataset.tab;
      if (key === active) return;
      const url = new URL(a.getAttribute('href'), window.location.origin);
      url.searchParams.delete('sort_by');
      url.searchParams.set('section_id', 'dc-search-count');
      fetch(url.toString())
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          const n = parseInt(html.replace(/<[^>]+>/g, '').trim(), 10);
          if (Number.isNaN(n)) return;
          counts[key] = n;
          const badge = a.querySelector('[data-count-for]');
          if (badge) badge.textContent = n;
          if (n === 0) a.classList.add('is-empty');
          renderEmptyAlt();
        })
        .catch(() => {});
    });

    // 4. Category suggestions (Products tab only, and only when there is something to narrow).
    const catsEl = document.querySelector('[data-dc-search-cats]');
    const activeCount = parseInt((nav.querySelector(`[data-count-for="${active}"]`) || {}).textContent, 10) || 0;
    if (catsEl && active === 'products' && terms && activeCount >= 10) {
      let suggestPath = nav.dataset.suggestUrl || '/search/suggest';
      if (!suggestPath.endsWith('.json')) suggestPath += '.json';
      const suggest = new URL(suggestPath, window.location.origin);
      suggest.searchParams.set('q', terms);
      suggest.searchParams.set('resources[type]', 'collection');
      suggest.searchParams.set('resources[limit]', '10');
      suggest.searchParams.set('resources[limit_scope]', 'each');
      fetch(suggest.toString())
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => {
          const cols = (json && json.resources && json.resources.results && json.resources.results.collections) || [];
          const seen = new Set();
          const list = catsEl.querySelector('ul');
          cols.forEach((c) => {
            const key = c.title.trim().toLowerCase();
            if (seen.has(key) || seen.size >= 6) return;
            seen.add(key);
            const li = document.createElement('li');
            const link = document.createElement('a');
            link.className = 'dc-search-cats__chip';
            link.href = c.url;
            link.textContent = c.title;
            li.appendChild(link);
            list.appendChild(li);
          });
          catsEl.hidden = seen.size === 0;
        })
        .catch(() => {});
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
